/**
 * The application's basic blocks, drawn by the UI kit (@easymusic/ui).
 *
 * Pages import them from here, not from the kit: this file is the one place that
 * says which kit component stands behind each of them.
 */
import {
  Alert as KitAlert,
  Card as KitCard,
  Checkbox as KitCheckbox,
  Chip,
  DataTable,
  Progress,
  Radio as KitRadio,
  SearchField as KitSearchField,
  Select as KitSelect,
  Slider as KitSlider,
  Spinner as KitSpinner,
  TextArea as KitTextArea,
  TextField as KitTextField,
  Toggle as KitToggle,
  type AlertTone,
  type ChipTone,
  type DataColumn,
  type DataTableProps,
  type IconName,
  type SelectProps,
} from '@easymusic/ui';
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  // plain: the pages space the content of their cards themselves.
  return <KitCard layout="plain" radius="md" className={className} {...props} />;
}

export function Spinner({ label }: { label: string }) {
  return <KitSpinner label={label} />;
}

type Tone = 'info' | 'ok' | 'bad' | 'warn';

const ALERT_TONES: Record<Tone, AlertTone> = { info: 'info', ok: 'success', bad: 'danger', warn: 'warning' };

interface AlertProps {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Announce to screen readers as soon as it appears. */
  live?: boolean;
}

export function Alert({ tone = 'info', ...props }: AlertProps) {
  return <KitAlert tone={ALERT_TONES[tone]} {...props} />;
}

type BadgeTone = Tone | 'neutral';

const BADGE_TONES: Record<BadgeTone, ChipTone> = { info: 'focus', ok: 'success', bad: 'danger', warn: 'warning', neutral: 'locked' };

/** A short label of a state or a kind: "Official", "Blocked", "In progress". */
export function Badge({ tone = 'neutral', icon, className, children }: { tone?: BadgeTone; icon?: IconName; className?: string; children: ReactNode }) {
  return (
    <Chip tone={BADGE_TONES[tone]} icon={icon} className={className}>
      {children}
    </Chip>
  );
}

/** `value` is a share: 0 … 1. */
export function ProgressBar({ value, label }: { value: number; label: string }) {
  return <Progress value={Math.min(1, Math.max(0, value)) * 100} label={label} tone="lavender" />;
}

/**
 * One line of text with its label. A password field has a button that shows what was
 * typed, named in the reader's language.
 */
export function TextField(props: ComponentProps<typeof KitTextField>) {
  const { t } = useTranslation('common');
  return <KitTextField showPasswordLabel={t('field.showPassword')} hidePasswordLabel={t('field.hidePassword')} {...props} />;
}

/** Several lines of text with their label: a reason, a note, a comment. */
export function TextArea(props: ComponentProps<typeof KitTextArea>) {
  return <KitTextArea {...props} />;
}

/** Something that is so or is not, with its label; the whole line is pressed. */
export function Checkbox(props: ComponentProps<typeof KitCheckbox>) {
  return <KitCheckbox {...props} />;
}

/** One of several that exclude each other: those of a group share a `name`. */
export function Radio(props: ComponentProps<typeof KitRadio>) {
  return <KitRadio {...props} />;
}

/** A search box. Its label is its accessible name; it is not shown. */
export function SearchField(props: Omit<ComponentProps<typeof KitSearchField>, 'onVoice' | 'voiceLabel'>) {
  return <KitSearchField {...props} />;
}

/** A setting that takes effect at once: on or off, with its label and a line about it. */
export function Toggle(props: ComponentProps<typeof KitToggle>) {
  return <KitToggle {...props} />;
}

/** A value on a scale, set by dragging or with the arrows: the volume. */
export function Slider(props: ComponentProps<typeof KitSlider>) {
  return <KitSlider {...props} />;
}

/** One choice from a short list, in a list drawn by the kit (not the system's). */
export function Select<Value extends string>(props: SelectProps<Value>) {
  return <KitSelect {...props} />;
}

/** A column of a `Table`: its header, and what it shows of a row. */
export type TableColumn<Row> = DataColumn<Row>;

/** Records in rows, their fields in columns: lists that are compared and scanned. */
export function Table<Row>(props: DataTableProps<Row>) {
  return <DataTable {...props} />;
}

export function PageTitle({ children, subtitle }: { children: ReactNode; subtitle?: ReactNode }) {
  return (
    <header className="mb-6">
      <h1 className="hs-display">{children}</h1>
      {subtitle ? <p className="hs-body-l mt-2 text-muted">{subtitle}</p> : null}
    </header>
  );
}
